/**
 * 角色卡社区「可插拔来源」接口（T07b）。
 *
 * 社区在本任务里**只做前端侧**：把「社区」抽象成一个 `CommunitySource`
 * （来源适配器），默认内置一个随包 JSON 目录，并允许用户填入一个返回 JSON 的
 * 远端目录地址。真实的社区后端 / 账号体系 / 上传审核不在本任务范围内。
 *
 * 约定（远端 HTTP 来源）：
 * - 列表：`GET {baseUrl}/cards`，返回 JSON 数组，或 `{ "cards": [...] }`；
 * - 详情：`GET {baseUrl}/cards/{id}`，返回一张卡对象，或 `{ "card": {...} }`。
 *
 * 数据校验立场（任务要求「非法数据不得进入 UI」，二选一后固定为）：
 * - 列表里的**单条脏数据被丢弃**（不进入 UI），不会拖垮整份列表；
 * - 整个响应结构非法、或详情卡非法时，抛出可识别的 `CommunitySourceError`
 *   （`code === 'invalid-data'`），由页面转成「加载失败」状态。
 */
import type { Preset, WorldBookEntry, WorldBookSelectiveLogic } from '../../core/types';
import type { FetchLike } from '../../core/providers/types';
import builtinCatalog from './builtinCatalog.json';

/** 列表用的卡片摘要（不含 systemPrompt / 世界书正文）。 */
export interface CommunityCardSummary {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  avatar?: string;
  author?: string;
  /** 最后更新时间（epoch 毫秒）。 */
  updatedAt: number;
}

/** 社区来源适配器：注册表按 id 持有实例。 */
export interface CommunitySource {
  readonly id: string;
  readonly label: string;
  list(query?: string): Promise<CommunityCardSummary[]>;
  fetchCard(id: string): Promise<Preset>;
}

/** 可识别的来源错误：页面据此区分「加载失败」的不同原因。 */
export type CommunitySourceErrorCode = 'invalid-data' | 'network' | 'not-found';

export class CommunitySourceError extends Error {
  readonly code: CommunitySourceErrorCode;

  constructor(code: CommunitySourceErrorCode, message: string) {
    super(message);
    this.name = 'CommunitySourceError';
    this.code = code;
  }
}

/* -------------------------------------------------------------------------- *
 * 字段校验
 * -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** 严格校验并归一化一条卡片摘要；非法时抛 `invalid-data`。 */
export function parseCardSummary(raw: unknown): CommunityCardSummary {
  if (!isRecord(raw)) {
    throw new CommunitySourceError('invalid-data', '卡片摘要必须是对象');
  }
  if (!isNonEmptyString(raw.id)) {
    throw new CommunitySourceError('invalid-data', '卡片缺少数值合法的 id');
  }
  if (!isNonEmptyString(raw.name)) {
    throw new CommunitySourceError('invalid-data', `卡片 ${raw.id} 缺少 name`);
  }
  const rawTags = raw.tags as unknown[];
  if (!Array.isArray(rawTags) || rawTags.some((tag) => typeof tag !== 'string')) {
    throw new CommunitySourceError('invalid-data', `卡片 ${raw.id} 的 tags 必须是字符串数组`);
  }
  const tags = rawTags.filter((tag): tag is string => typeof tag === 'string');
  if (typeof raw.updatedAt !== 'number' || !Number.isFinite(raw.updatedAt)) {
    throw new CommunitySourceError('invalid-data', `卡片 ${raw.id} 的 updatedAt 必须是数字`);
  }

  return {
    id: raw.id,
    name: raw.name,
    tags,
    updatedAt: raw.updatedAt,
    ...(optionalString(raw.description) === undefined
      ? {}
      : { description: optionalString(raw.description) }),
    ...(optionalString(raw.avatar) === undefined ? {} : { avatar: optionalString(raw.avatar) }),
    ...(optionalString(raw.author) === undefined ? {} : { author: optionalString(raw.author) }),
  };
}

/** 列表层校验：丢弃非法条目，只保留能进入 UI 的数据。 */
export function parseCardSummaryList(raw: unknown): CommunityCardSummary[] {
  if (!Array.isArray(raw)) {
    throw new CommunitySourceError('invalid-data', '目录响应必须是数组');
  }
  const result: CommunityCardSummary[] = [];
  for (const entry of raw) {
    try {
      result.push(parseCardSummary(entry));
    } catch {
      // 单条脏数据直接丢弃，不进入 UI。
    }
  }
  return result;
}

function parseWorldBook(raw: unknown): WorldBookEntry[] | undefined {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) return undefined;
  const entries: WorldBookEntry[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    if (!isNonEmptyString(entry.id) || !isNonEmptyString(entry.content)) continue;
    const keywords = Array.isArray(entry.keywords)
      ? entry.keywords.filter((keyword): keyword is string => typeof keyword === 'string')
      : [];
    const logics: readonly WorldBookSelectiveLogic[] = ['andAny', 'andAll', 'notAny', 'notAll'];
    const selectiveLogic = logics.includes(entry.selectiveLogic as WorldBookSelectiveLogic)
      ? entry.selectiveLogic as WorldBookSelectiveLogic : undefined;
    if (entry.selectiveLogic !== undefined && selectiveLogic === undefined) continue;
    if (entry.secondaryKeywords !== undefined && (!Array.isArray(entry.secondaryKeywords)
      || entry.secondaryKeywords.some((keyword) => typeof keyword !== 'string'))) continue;
    if (['constant', 'caseSensitive', 'matchWholeWords'].some((field) => entry[field] !== undefined
      && typeof entry[field] !== 'boolean')) continue;
    entries.push({
      id: entry.id,
      keywords,
      content: entry.content,
      enabled: entry.enabled === undefined ? true : entry.enabled === true,
      priority: typeof entry.priority === 'number' && Number.isFinite(entry.priority) ? entry.priority : 1,
      ...(typeof entry.constant === 'boolean' ? { constant: entry.constant } : {}),
      ...(typeof entry.name === 'string' ? { name: entry.name } : {}),
      ...(selectiveLogic === undefined ? {} : { selectiveLogic }),
      ...(entry.secondaryKeywords === undefined ? {} : { secondaryKeywords: entry.secondaryKeywords as string[] }),
      ...(typeof entry.caseSensitive === 'boolean' ? { caseSensitive: entry.caseSensitive } : {}),
      ...(typeof entry.matchWholeWords === 'boolean' ? { matchWholeWords: entry.matchWholeWords } : {}),
    });
  }
  return entries;
}

/** 严格校验一张完整角色卡（详情）；非法时抛 `invalid-data`。 */
export function parsePreset(raw: unknown): Preset {
  if (!isRecord(raw)) {
    throw new CommunitySourceError('invalid-data', '角色卡必须是对象');
  }
  if (!isNonEmptyString(raw.id)) {
    throw new CommunitySourceError('invalid-data', '角色卡缺少数值合法的 id');
  }
  if (!isNonEmptyString(raw.name)) {
    throw new CommunitySourceError('invalid-data', `角色卡 ${raw.id} 缺少 name`);
  }
  if (typeof raw.systemPrompt !== 'string') {
    throw new CommunitySourceError('invalid-data', `角色卡 ${raw.id} 缺少 systemPrompt`);
  }
  const tags = Array.isArray(raw.tags)
    ? raw.tags.filter((tag): tag is string => typeof tag === 'string')
    : undefined;
  const worldBook = parseWorldBook(raw.worldBook);

  return {
    id: raw.id,
    name: raw.name,
    systemPrompt: raw.systemPrompt,
    source: 'community',
    ...(optionalString(raw.description) === undefined
      ? {}
      : { description: optionalString(raw.description) }),
    ...(optionalString(raw.avatar) === undefined ? {} : { avatar: optionalString(raw.avatar) }),
    ...(tags === undefined ? {} : { tags }),
    ...(worldBook === undefined ? {} : { worldBook }),
  };
}

/* -------------------------------------------------------------------------- *
 * 内置目录来源
 * -------------------------------------------------------------------------- */

/** 内置目录里的一张完整卡（Preset + 社区摘要字段）。 */
type BuiltinCard = Preset & { author?: string; updatedAt: number };

/** 内置来源的稳定 id（注册表与页面都靠它拿默认来源）。 */
export const BUILTIN_SOURCE_ID = 'builtin';

function toSummary(card: BuiltinCard): CommunityCardSummary {
  const summary: CommunityCardSummary = {
    id: card.id,
    name: card.name,
    tags: [...(card.tags ?? [])],
    updatedAt: card.updatedAt,
  };
  if (card.description !== undefined) summary.description = card.description;
  if (card.avatar !== undefined) summary.avatar = card.avatar;
  if (card.author !== undefined) summary.author = card.author;
  return summary;
}

function matchesQuery(card: BuiltinCard, query: string): boolean {
  const haystack = [card.name, card.description ?? '', ...(card.tags ?? [])]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

/** 读取随包内置目录（JSON -> 已校验的卡片数组）；任一条非法即抛 `invalid-data`。 */
export function readBuiltinCatalog(): BuiltinCard[] {
  if (!Array.isArray(builtinCatalog)) {
    throw new CommunitySourceError('invalid-data', '内置目录 JSON 结构非法');
  }
  return builtinCatalog.map((entry, index) => {
    let summary: CommunityCardSummary;
    let preset: Preset;
    try {
      summary = parseCardSummary(entry);
      preset = parsePreset(entry);
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      throw new CommunitySourceError('invalid-data', `内置目录第 ${index + 1} 项非法：${detail}`);
    }
    const author = isRecord(entry) ? optionalString(entry.author) : undefined;
    return {
      ...preset,
      tags: summary.tags,
      updatedAt: summary.updatedAt,
      ...(summary.description === undefined ? {} : { description: summary.description }),
      ...(summary.avatar === undefined ? {} : { avatar: summary.avatar }),
      ...(author === undefined ? {} : { author }),
    };
  });
}

/** 创建一个读取随包 JSON 的内置目录来源。 */
export function createBuiltinSource(): CommunitySource {
  const cards = readBuiltinCatalog();
  return {
    id: BUILTIN_SOURCE_ID,
    label: '内置目录',
    list(query?: string): Promise<CommunityCardSummary[]> {
      const needle = query?.trim().toLowerCase() ?? '';
      const filtered = needle === '' ? cards : cards.filter((card) => matchesQuery(card, needle));
      return Promise.resolve(filtered.map(toSummary));
    },
    fetchCard(id: string): Promise<Preset> {
      const card = cards.find((entry) => entry.id === id);
      if (card === undefined) {
        return Promise.reject(new CommunitySourceError('not-found', `内置目录里没有角色卡 ${id}`));
      }
      return Promise.resolve(parsePreset(card));
    },
  };
}

/* -------------------------------------------------------------------------- *
 * 远端 HTTP 目录来源
 * -------------------------------------------------------------------------- */

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${path}`;
}

/** HTTP 状态 -> 可识别错误。 */
function httpError(status: number, url: string): CommunitySourceError {
  if (status === 404) return new CommunitySourceError('not-found', `远端目录没有该角色卡（${url}）`);
  return new CommunitySourceError('network', `远端目录请求失败：HTTP ${status}（${url}）`);
}

async function readJson(response: Response, url: string): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    throw new CommunitySourceError('invalid-data', `远端目录返回的不是合法 JSON（${url}）`);
  }
}

/**
 * 创建一个从远端 HTTP 目录拉取 JSON 的来源。
 *
 * @param baseUrl 远端目录基址，例如 `https://example.com/community`
 * @param fetchImpl 可注入的 fetch（测试用；缺省取 `globalThis.fetch`）
 */
export function createHttpSource(baseUrl: string, fetchImpl?: FetchLike): CommunitySource {
  const base = baseUrl.trim().replace(/\/+$/, '');
  const doFetch: FetchLike =
    fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  const listUrl = joinUrl(base, '/cards');

  async function request(url: string): Promise<Response> {
    try {
      return await doFetch(url, { headers: { accept: 'application/json' } });
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      throw new CommunitySourceError('network', `无法连接远端目录：${detail}`);
    }
  }

  return {
    id: `http:${base}`,
    label: base,
    async list(query?: string): Promise<CommunityCardSummary[]> {
      const response = await request(listUrl);
      if (!response.ok) throw httpError(response.status, listUrl);
      const payload = await readJson(response, listUrl);
      const rawList = Array.isArray(payload)
        ? payload
        : isRecord(payload) && Array.isArray(payload.cards)
          ? payload.cards
          : null;
      if (rawList === null) {
        throw new CommunitySourceError('invalid-data', `目录响应结构非法（${listUrl}）`);
      }
      const summaries = parseCardSummaryList(rawList);
      const needle = query?.trim().toLowerCase() ?? '';
      if (needle === '') return summaries;
      return summaries.filter((card) =>
        [card.name, card.description ?? '', ...card.tags].join(' ').toLowerCase().includes(needle),
      );
    },
    async fetchCard(id: string): Promise<Preset> {
      const url = joinUrl(base, `/cards/${encodeURIComponent(id)}`);
      const response = await request(url);
      if (!response.ok) throw httpError(response.status, url);
      const payload = await readJson(response, url);
      const raw = isRecord(payload) && 'card' in payload ? payload.card : payload;
      return parsePreset(raw);
    },
  };
}
